import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { geminiGenerateJson } from '../../geminiService';
import { AI_PIPELINE_SCHEMA_V1 } from '../../../constants/aiPipelineModes';

/**
 * Schema v1 distiller — titles as claim checklist only; do not invent proof from SEF.
 * Does not open file bytes (per-file content analysis is a later P2 step on this pipeline).
 */
export async function distillEvidenceSchemaV1(schoolId) {
  const auditRef = doc(db, 'audits', schoolId);
  const distilledRef = doc(db, 'distilledData', schoolId);

  const auditSnap = await getDoc(auditRef);
  const { evidenceVault = [], stations = {} } = auditSnap.data() || {};

  const claimChecklist = Object.keys(stations || {}).map((id) => ({
    stationId: id,
    selectedLevel: stations[id]?.selectedLevel ?? null,
    narrativeExcerpt: String(stations[id]?.narrative || '').slice(0, 500),
  }));

  const prompt = `
You are the IFIP Evidence Verification Distiller (schema_v1 pipeline).

You have NOT opened the files. You only see filenames and a claim checklist from the SEF.

INPUT:
- Filenames: ${(evidenceVault || []).map((f) => f.name).join(', ') || '(none)'}
- Station tags (if present on files): ${JSON.stringify(
    (evidenceVault || []).map((f) => ({ name: f.name, tags: f.tags || [] }))
  )}
- Claim checklist (SEF) — use ONLY to test what titles might verify; NEVER as a source of what a file proves:
${JSON.stringify(claimChecklist)}

TASK:
1. For EVERY file, describe only what the TITLE suggests the document is (evidenceSummary).
2. For "proves": state only what the title could reasonably be expected to verify — do NOT invent proof language from the school narrative.
3. Categorize: Financial, Policy, Pedagogical, Physical/Sensory, or Photographic (if image-like extension).
4. strength 1-5 for how strongly the title alone could support a typical claim of that document type.
5. If filename looks like a photo/image (jpg/png/gif/webp/heic), set category Photographic, proves to "Human review only — photographs are never AI-analysed", strength 0 or 1.

OUTPUT: Return ONLY raw JSON:
{
  "factSheet": [
    { "fileName": "exact_original_filename.pdf", "category": "", "evidenceSummary": "", "proves": "", "strength": 1 }
  ],
  "anonymizedSummary": "Up to 200 words on what the TITLE inventory suggests — verification-oriented, not a blended school story.",
  "globalMetrics": { "totalFiles": 0, "primaryCategory": "" },
  "pipeline": "schema_v1"
}
`;

  const distillation = await geminiGenerateJson(prompt);

  await setDoc(distilledRef, {
    ...distillation,
    pipeline: AI_PIPELINE_SCHEMA_V1,
    distilledAt: serverTimestamp(),
    sourceAuditVersion: auditSnap.data()?.lastUpdated || null,
  });

  return distillation;
}